import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CapacityModel } from './capacity.model'
import {
  fetchDateRangeActiveCapacities,
  fetchLogisticElementsToItems,
  fetchTodayActiveCapacities,
  filterCapacityApplicableActionsConfiguration,
  filterCapacityApplicableSecondaryActionsConfiguration,
  updateLockState
} from './capacity.service'

import moment from 'moment'

import { Pageview } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  EnumBloqueoCapacidad,
  EnumEstadoProductoCapacidad,
  EnumServicio,
  EnumTipoNaturaleza,
  EnumTipoProductoCapacidad
} from '../../api'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import {
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues,
  enumValueToKey
} from '../../helpers'
import {
  changeActionIsLoading,
  generateIconsFromConfiguration
} from '../../helpers/dataTableActionIconHelper'
import { normalizeFilename } from '../../helpers/fileDownloadHelper'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { useHttpError } from '../../hooks/useHttpError'
import { useReferredState } from '../../hooks/useReferredState'
import {
  ActionIcons,
  DateFilters,
  DatePeriod,
  GlobalNotification,
  NonAuthRoutes
} from '../../model'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { Pages } from '../../model/RBAC/constants'
import {
  getCustomerDomain,
  RootStoreModel,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../redux'
import { servicesFilter } from '../capacity/capacity.service'

export interface CapacityPageProps {}

export const CapacityPage: React.FC<CapacityPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'capacity', 'enums'])
  const { info } = useNotify()
  let history = useHistory()

  const { dataTableFilter, dataTableSearch, dateFilter, customFilter } = usePageState()

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [capacities, capacitiesRef, setCapacities] = useReferredState<CapacityModel[] | undefined>(
    undefined
  )

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter ? moment(dateFilter.initDate).toDate() : moment().toDate(),
    end: dateFilter ? moment(dateFilter.endDate).toDate() : moment().toDate()
  })

  const [dateRangeFilter, setDateRangeFilter] = React.useState<DateFilters>(
    dateFilter?.activeDateFilter || DateFilters.ACTIVES
  )

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [logisticElementsForCustomFilter, setLogisticElementsForCustomFilter] = React.useState<
    SelectOption[]
  >([])

  //#region Hooks

  const { showError, clearError, errorProps } = useHttpError()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const getCapacityList = async () => {
    setCapacities(
      await fetchCapacities(
        customFilter?.startDate ? customFilter?.startDate! : dateRange.start,
        customFilter?.endDate ? customFilter?.endDate! : dateRange.end,
        dateRangeFilter
      )
    )
  }

  useEffect(() => {
    getCapacityList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeCompany, activeBalanceZone])

  //#endregion

  //#region DataTable definition

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'actions',
        type: DataType.HTML,
        columnWidth: 30
      },
      {
        name: 'initDate',
        label: t('capacity:dataTable.columns.initDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('capacity:dataTable.columns.endDate'),
        type: DataType.DATE
      },
      {
        name: 'secondaryActions',
        type: DataType.HTML,
        columnWidth: 25
      },
      {
        name: 'code',
        label: t('capacity:dataTable.columns.code'),
        type: DataType.TEXT
      },
      {
        name: 'service',
        label: t('capacity:dataTable.columns.service'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArrayDefaultValues(
          EnumServicio,
          'enums:EnumServicio',
          servicesFilter
        )
      },
      {
        name: 'logisticElement.description',
        label: t('capacity:dataTable.columns.logisticElement'),
        type: DataType.TEXT,
        columnWidth: 50
      },
      {
        name: 'capacity',
        label: t('capacity:dataTable.columns.capacity'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.ENERGY
      },
      {
        name: 'premium',
        label: t('capacity:dataTable.columns.premium'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.OTHERS
      },
      {
        name: 'duration',
        label: t('capacity:dataTable.columns.duration'),
        type: DataType.SELECT,
        hideForResponsive: true,
        selectOptions: enumToSelectOptionArray(
          EnumTipoProductoCapacidad,
          'enums:EnumTipoProductoCapacidad'
        )
      },
      {
        name: 'nature',
        label: t('capacity:dataTable.columns.nature'),
        type: DataType.SELECT,
        hideForResponsive: true,
        selectOptions: enumToSelectOptionArray(EnumTipoNaturaleza, 'enums:EnumTipoNaturaleza')
      },
      {
        name: 'signDate',
        hideForResponsive: true,
        label: t('capacity:dataTable.columns.signDate'),
        type: DataType.DATE
      },
      {
        name: 'productCapacityState',
        label: t('capacity:dataTable.columns.productCapacityState'),
        type: DataType.SELECT,
        hideForResponsive: true,
        selectOptions: enumToSelectOptionArray(
          EnumEstadoProductoCapacidad,
          'enums:EnumEstadoProductoCapacidad'
        )
      }
    ]
  }, [t])

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 1,
    search: true,
    filterType: FilterType.ADVANCED,
    download: true,
    filename: normalizeFilename(t('capacity:title')),
    print: false,
    selectableRows: false,
    customInsertEditModal: true,
    canEdit: false,
    canDelete: false,
    onRowInsert: async () => {
      history.push(NonAuthRoutes.capacityAdd)
      return true
    },
    analytics: {
      customer: getCustomerDomain(),
      view: t('capacity:dataTable.analytics')
    },
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Pageview fontSize="small" />,
        tooltip: t('capacity:dataTable.tooltips.view'),
        onClick: (row: CapacityModel) => {
          history.push(
            row.lockState === EnumBloqueoCapacidad.BloqueadoBundle
              ? NonAuthRoutes.capacityEditBundle
              : row.productCapacityState === EnumEstadoProductoCapacidad.Formalizada
                ? NonAuthRoutes.capacityEditFormalized
                : NonAuthRoutes.capacityEdit,
            { idCapacity: row.id }
          )
        }
      },
      {
        userPermission: UserPermission.READ_ONLY,
        icon: <Pageview fontSize="small" />,
        tooltip: t('capacity:dataTable.tooltips.view'),
        onClick: (row: CapacityModel) => {
          history.push(
            row.lockState === EnumBloqueoCapacidad.BloqueadoBundle
              ? NonAuthRoutes.capacityEditBundle
              : NonAuthRoutes.capacityEditFormalized,
            { idCapacity: row.id }
          )
        },
        visibleIf: 'canViewFormalized'
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  //#endregion

  //#region Handlers

  const onLockActionHandler = React.useCallback(async (item: CapacityModel) => {
    getConfirmation(ConfirmationDialogs.LOCK).then(async () => {
      await changeCapacityLockState(item, EnumBloqueoCapacidad.BloqueadoUsuario, ActionIcons.UNLOCK)
      closeConfirmation()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onUnlockActionHandler = React.useCallback(async (item: CapacityModel) => {
    getConfirmation(ConfirmationDialogs.UNLOCK).then(async () => {
      await changeCapacityLockState(item, EnumBloqueoCapacidad.Libre, ActionIcons.LOCK)
      closeConfirmation()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    activeDateFilter && setDateRangeFilter(activeDateFilter)
    setDateRange({ start: startDate, end: endDate })
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  //#endregion

  //#region Functions

  const updateDataTableRowData = (row: CapacityModel) => {
    if (capacitiesRef.current) {
      const index = capacitiesRef.current.findIndex(x => x.id === row.id)

      if (index !== -1) {
        setCapacities([
          ...capacitiesRef.current!.slice(0, index),
          completeDataTableActions({ ...row }),
          ...capacitiesRef.current!.slice(index + 1)
        ])
      }
    }
  }

  const updateDateTableRowCapacityData = (row: CapacityModel, state: EnumBloqueoCapacidad) => {
    if (capacitiesRef.current) {
      const index = capacitiesRef.current.findIndex(x => x.id === row?.id)
      if (index !== -1) {
        capacitiesRef.current[index].lockState = state
        setCapacities(
          capacitiesRef.current.map(item => {
            return completeDataTableActions(item)
          })
        )
      }
    }
  }

  const changeCapacityLockState = async (
    item: CapacityModel,
    lock: EnumBloqueoCapacidad,
    icon: ActionIcons
  ): Promise<void> => {
    if (item.lock !== undefined) {
      updateDataTableRowData({
        ...item,
        actionsConfiguration: changeActionIsLoading(item.actionsConfiguration, icon, true)
      })

      await updateLockState(item.id!, lock).then(success => {
        if (success) {
          lock === EnumBloqueoCapacidad.Libre
            ? info(t('capacity:notifications.success.unlock'))
            : info(t('capacity:notifications.success.lock'))
          updateDateTableRowCapacityData(
            {
              ...item,
              actionsConfiguration: changeActionIsLoading(item.actionsConfiguration, icon, false)
            },
            lock
          )
        }
        return
      })
    }

    return
  }

  const fetchCapacities = async (
    startDate: Date,
    endDate: Date,
    dateRangeFilter: DateFilters,
    service?: number,
    logisticElement?: number,
    duration?: number,
    nature?: number
  ): Promise<CapacityModel[] | undefined> => {
    setIsFetching(true)
    setCapacities(undefined)

    const response =
      dateRangeFilter === DateFilters.ACTIVES
        ? await fetchTodayActiveCapacities(startDate, endDate)
        : await fetchDateRangeActiveCapacities(
            startDate,
            endDate,
            service,
            logisticElement,
            duration,
            nature
          )

    setIsFetching(false)

    if (response.success) {
      clearError()
      if (response.data !== undefined) {
        return response.data?.map((item: CapacityModel) => {
          return completeDataTableActions(item)
        })
      } else {
        return []
      }
    } else {
      response.error &&
        showError({
          errorCode: response.error.code,
          onRefresh: async () => {
            setCapacities(
              await fetchCapacities(
                customFilter?.startDate ? customFilter?.startDate! : dateRange.start,
                customFilter?.endDate ? customFilter?.endDate! : dateRange.end,
                dateRangeFilter
              )
            )
          }
        })
      return []
    }
  }

  const completeDataTableActions = React.useCallback(
    (item: CapacityModel): CapacityModel => {
      return {
        ...item,
        actions:
          item.actionsConfiguration &&
          item.actionsConfiguration.length > 0 &&
          generateIconsFromConfiguration(
            item,
            filterCapacityApplicableActionsConfiguration(item, item.actionsConfiguration),
            new Map<ActionIcons, Function>([
              [ActionIcons.LOCK, onUnlockActionHandler],
              [ActionIcons.UNLOCK, onLockActionHandler]
            ])
          ),
        secondaryActions:
          item.secondaryActionsConfiguration &&
          item.secondaryActionsConfiguration.length > 0 &&
          generateIconsFromConfiguration(
            item,
            filterCapacityApplicableSecondaryActionsConfiguration(
              item,
              item.secondaryActionsConfiguration
            ),
            new Map<ActionIcons, Function>([])
          )
      }
    },
    [onUnlockActionHandler, onLockActionHandler]
  )

  const countCapacities = async (
    startDate: Date,
    endDate: Date,
    service?: number,
    logisticElement?: number,
    duration?: number,
    nature?: number
  ): Promise<number | undefined> => {
    return await await fetchDateRangeActiveCapacities(
      startDate,
      endDate,
      service,
      logisticElement,
      duration,
      nature
    ).then(response => {
      if (response.success) {
        if (response.data !== undefined) {
          return response.data.length
        } else {
          return 0
        }
      } else {
        return undefined
      }
    })
  }

  const searchFilterInputs = [
    {
      name: 'service',
      label: t('capacity:dataTable.columns.service'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumServicio,
        'enums:EnumServicio',
        servicesFilter
      ),
      onChange: async (value: any, formState: object) => {
        setLogisticElementsForCustomFilter(
          await fetchLogisticElementsToItems([enumValueToKey(EnumServicio, value)])
        )
      }
    },
    {
      name: 'logisticElement',
      label: t('capacity:dataTable.columns.logisticElement'),
      type: DataType.SELECT,
      selectOptions: logisticElementsForCustomFilter,
      helpText: t('capacity:forms.edit.helpTexts.logisticElement')
    },
    {
      name: 'duration',
      label: t('capacity:dataTable.columns.duration'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoProductoCapacidad,
        'enums:EnumTipoProductoCapacidad'
      )
    },
    {
      name: 'nature',
      label: t('capacity:dataTable.columns.nature'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoNaturaleza, 'enums:EnumTipoNaturaleza')
    }
  ]

  const onApplyCustomFilter = async (filterToApply: any) => {
    setCapacities(
      await fetchCapacities(
        filterToApply.startDate,
        filterToApply.endDate,
        DateFilters.CUSTOM,
        filterToApply.service,
        filterToApply.logisticElement,
        filterToApply.duration,
        filterToApply.nature
      )
    )
    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const operationsCount = await countCapacities(
      filterToApply.startDate,
      filterToApply.endDate,
      filterToApply.service,
      filterToApply.logisticElement,
      filterToApply.duration,
      filterToApply.nature
    )
    if (operationsCount !== undefined && operationsCount < 1000) {
      return true
    }
    return false
  }

  //#endregion

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('capacity:title')}>
        <DateFilter
          inactive={customFilter}
          enabled={!isFetching}
          initialFilter={
            dateFilter?.activeDateFilter !== null || dateFilter?.activeDateFilter !== undefined
              ? dateRangeFilter
              : dateFilter.activeDateFilter
          }
          onSelectDate={onSelectDateHandler}
          filters={[{ filter: DateFilters.ACTIVES }]}
        ></DateFilter>
        <CustomFilter
          onApplyFilter={onApplyCustomFilter}
          onPrevalidateFilter={onPrevalidateCustomFilter}
          additionalFilters={searchFilterInputs}
          appliedFilter={customFilter}
          enabled={!isFetching}
        ></CustomFilter>
      </PageHeader>
      <DataTable
        data={capacities}
        columns={columns}
        options={{
          ...options,
          analytics: {
            customer: getCustomerDomain(),
            view: t('capacity:dataTable.analytics')
          }
        }}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Capacities)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />
    </Page>
  )
}
